import { classNames, transformDateToLocalDateString } from "helpers";
import { RegistrationExpiryRow } from "services/api/fleet/registrations/types";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import VehicleCell from "ui/components/Table/TableSearch/components/Cells/VehicleCell";
import Tooltip from "ui/components/Tooltip/Tooltip";

// Hrvatska množina za "dan": 1 dan, ostalo dana.
const dayLabel = (days: number): string => (days === 1 ? "dan" : "dana");

interface ExpiryStatus {
  className: string;
  title: string;
  message: string;
}

// Status i boja ćelije datuma isteka:
// istekao -> svijetlo crvena, unutar mjesec dana -> svijetlo žuta, inače bez boje.
const getExpiryStatus = (expiryDate: Date | null): ExpiryStatus => {
  if (!expiryDate) {
    return {
      className: "text-slate-900",
      title: "Bez datuma",
      message: "Datum isteka nije unesen.",
    };
  }

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const expiry = new Date(expiryDate);
  expiry.setHours(0, 0, 0, 0);

  const oneMonthAhead = new Date(startOfToday);
  oneMonthAhead.setMonth(oneMonthAhead.getMonth() + 1);

  const diffDays = Math.round(
    (expiry.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (expiry < startOfToday) {
    const daysAgo = Math.abs(diffDays);
    return {
      className: "bg-red-100 text-red-700",
      title: "Istekao",
      message: `Isteklo prije ${daysAgo} ${dayLabel(daysAgo)}.`,
    };
  }

  if (expiry <= oneMonthAhead) {
    return {
      className: "bg-yellow-100 text-yellow-800",
      title: "Pred istek",
      message:
        diffDays === 0
          ? "Ističe danas."
          : `Ističe za ${diffDays} ${dayLabel(diffDays)} (unutar mjesec dana).`,
    };
  }

  return {
    className: "text-slate-900",
    title: "Važeći",
    message: `Ističe za ${diffDays} ${dayLabel(diffDays)}.`,
  };
};

export const REGISTRATION_EXPIRY_SEARCH_COLUMNS = (
  onVehicleClick: (event: React.MouseEvent, vehicleId: string) => void
): Column[] => [
  {
    title: "Vozilo",
    field: "",
    type: ColumnTypes.custom,
    render: (row: RegistrationExpiryRow) => {
      if (!row.vehicle) return null;
      return (
        <VehicleCell
          vehicle={row.vehicle}
          onClick={(e) => onVehicleClick(e, row.vehicle.id)}
        />
      );
    },
  },
  {
    title: "Registracija",
    field: "",
    type: ColumnTypes.custom,
    render: (row: RegistrationExpiryRow) => (
      <div className="text-sm text-slate-900 truncate">
        {row.registrationPlate ?? ""}
      </div>
    ),
  },
  {
    title: "Datum isteka registracije",
    field: "",
    type: ColumnTypes.custom,
    render: (row: RegistrationExpiryRow) => {
      const status = getExpiryStatus(row.expiryDate);
      return (
        <Tooltip
          decorate={
            <span
              className={classNames(
                "inline-block rounded px-2 py-1 text-sm font-medium",
                status.className
              )}
            >
              {row.expiryDate
                ? transformDateToLocalDateString(row.expiryDate)
                : "-"}
            </span>
          }
          position="bottom-right"
          size="medium"
        >
          <div className="min-w-[220px]">
            <div className="text-xs uppercase text-slate-400 mb-1 font-semibold">
              {status.title}
            </div>
            <div className="text-sm">{status.message}</div>
          </div>
        </Tooltip>
      );
    },
  },
];
